import { Card, Text, BlockStack, Box, InlineStack, ProgressBar, Grid } from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { SubscriptionData } from "../types";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";

interface StorageOptionsCardProps {
  data: SubscriptionData;
  clinicId: string;
}

export function StorageOptionsCard({ data, clinicId }: StorageOptionsCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { POST } = useApiRequest();

  // Storage upgrade mutation
  type StorageUpgradeResponse = { data?: { link?: string } };
  const storageUpgradeMutation = useMutation({
    mutationFn: (storageType: string) => {
      return POST<StorageUpgradeResponse>(
        `/api/healthcare-companies/${clinicId}/billing/subscription`,
        {
          productId: storageType,
          redirectURI: window.location.href,
          productType: "storage",
        }
      );
    },
    onSuccess: (response) => {
      // The API returns a redirect link in data.link
      if (response?.data?.link) {
        window.location.href = response.data.link;
      }
      // Also invalidate queries in case the redirect doesn't happen immediately
      queryClient.invalidateQueries({ 
        queryKey: ["subscription", clinicId] 
      });
    },
  });

  const { currentStorage, availableStorageAddons } = data;

  // Determine progress bar tone based on usage percentage
  const getProgressBarTone = (percentage: number): "primary" | "critical" => {
    if (percentage >= 95) return "critical";
    return "primary";
  };

  // Check if a storage option is currently active
  const isCurrentStorage = (storageSize: number) => {
    return storageSize === currentStorage.max;
  };

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">
          {t("settings.subscription.storage.title", { defaultValue: "Storage" })}
        </Text>
        
        {/* Current Storage Usage */}
        <BlockStack gap="200">
            <Text variant="bodyMd" as="p">
                {currentStorage.current} GB of {currentStorage.max} GB used
            </Text>
          
          <ProgressBar 
            progress={currentStorage.percent} 
            size="small" 
            tone={getProgressBarTone(currentStorage.percent)} 
          />
          
          <Text variant="bodyMd" as="p" tone="subdued">
            {t("settings.subscription.storage.description", { 
              defaultValue: "You can add more storage to your plan. Plans are billed annually. Storage is used to store all your patients' photos, documents and other media you have." 
            })}
          </Text>
        </BlockStack>

        {/* Available Storage Options */}
        <Box>
          <Grid columns={{ xs: 1, sm: 1, md: 1, lg: 12 }}>
            {availableStorageAddons.map((storage) => {
              const isCurrent = isCurrentStorage(storage.sizeInGB);
              return (
                <Grid.Cell key={storage.type} columnSpan={{ xs: 1, sm: 1, md: 1, lg: 3 }}>
                  <Box width="100%">
                    <button
                      style={{ 
                        width: "100%",
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: isCurrent || storageUpgradeMutation.isPending ? "not-allowed" : "pointer",
                        textAlign: "left",
                      }}
                    onClick={() => !isCurrent && !storageUpgradeMutation.isPending && storageUpgradeMutation.mutate(storage.type)}
                    disabled={isCurrent || storageUpgradeMutation.isPending}
                  >
                    <Card background={isCurrent ? "bg-surface-brand" : "bg-surface-secondary"}>
                      <BlockStack gap="500">
                        <Text variant="headingLg" as="h3">
                            {storage.label}
                        </Text>
                        <InlineStack gap="100" blockAlign="baseline">
                            <Text variant="headingLg" fontWeight="semibold" as="p">
                            {storage.pricing.priceLabel}
                            </Text>
                            <Text variant="bodySm" as="span" tone="subdued">
                            {t(`settings.subscription.period.${storage.pricing.period}`)}
                            </Text>
                        </InlineStack>
                      </BlockStack>
                    </Card>
                  </button>
                  </Box>
                </Grid.Cell>
              );
            })}
          </Grid>
        </Box>
      </BlockStack>
    </Card>
  );
} 
